import LoadingPage from '@components/LoadingPage';
import { observer } from 'mobx-react';
import React, { forwardRef, Suspense, useMemo } from 'react';
import ProductInfoTab from '../ProductInfoTab';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { HoveredButton } from '@ui';
import './index.scss';
import BestComment from '../BestComment';
import icons from '@icons';
import type { IconType } from '@root/src/types';
import { IsPrerender } from '@utils/check';

import dayjs from 'dayjs';
import type {
  ProductInfoI,
  ProductPageMethods,
  ProductPageState,
  SortListI
} from '@routes/ProductPage/index.type';
import { CommentsTypeTypeEnum } from '@schema/cart';
import useWindowSize from '@customHooks/useWindowSize';

const {
  video: { black: videoBlackOutlined, white: videoWhiteOutlined },
  info: { black: infoBlackOutlined, white: infoWhiteOutlined },
  qmCircle: { black: qcBlackOutlined, white: qcWhiteOutlined },
  chiefHat: { black: chBlackOutlined, white: chWhiteOutlined }
} = icons.Outline;
const {
  star_rounded: { yellow: starYellowOutlined }
} = icons.Fill;

const CommentsBlock = React.lazy(
  async () =>
    await import('@ui').then(module => ({ default: module.CommentsBlock }))
);
const ProductPageRecipesTab = React.lazy(
  async () => await import('../ProductPageRecipesTab')
);
const VideoTab = React.lazy(async () => await import('../VideoTab'));

interface TabItem {
  id: number;
  title: string | React.ReactElement<any>;
  icon: IconType;
  iconHover: IconType;
  alt: string;
}

type PropsFromProductInfoI = Pick<
  NonNullable<ProductInfoI>,
  | 'mostUsefulComment'
  | 'weight_of_goods'
  | 'measureItem'
  | 'country'
  | 'productPackage'
  | 'nutritional_value'
  | 'energy_value'
  | 'ingredients'
  | 'isDigital'
  | 'storage_conditions'
  | 'gost'
  | 'documents'
  | 'text'
  | 'mostUsefulComment'
  | 'isVesovoi'
  | 'supplier_id'
  | 'videos'
  | 'averageRating'
>;

type PPTabsProps = PropsFromProductInfoI &
  Pick<
    ProductPageState,
    | 'productRecipes'
    | 'activeTab'
    | 'isProductHasAttachmentInComments'
    | 'setSort'
    | 'isProductAvailableForRefund'
  > & {
    attributes?: NonNullable<ProductInfoI>['attributes'];
    id: string;
    isBarMenu: boolean;
    how_to_make: string;
    carbs: string | null | undefined;
    reference_weight: string | null | undefined;
    proteins: string | null | undefined;
    fats: string | null | undefined;
    commentTotalCount: number;
    reviewTotalCount: number;
    isMobile?: boolean;
    sortlist: SortListI;
    linkToQuestions: () => void;
    commentsResolver: ProductPageMethods['_commentsResolver'];
    changeSort: ProductPageMethods['changeSort'];
    setActiveTab: ProductPageMethods['setActiveTab'];
  };

const PPTabs = forwardRef<HTMLDivElement, PPTabsProps>(
  (
    {
      carbs,
      proteins,
      reference_weight,
      fats,
      id,
      how_to_make,
      activeTab,
      measureItem,
      weight_of_goods,
      country,
      attributes,
      productPackage,
      nutritional_value,
      energy_value,
      ingredients,
      storage_conditions,
      gost,
      documents,
      isDigital,
      text,
      sortlist,
      setSort,
      isProductAvailableForRefund,
      productRecipes,
      videos,
      isMobile,
      mostUsefulComment,
      averageRating,
      reviewTotalCount,
      commentTotalCount,
      isBarMenu,
      supplier_id,
      isVesovoi,
      isProductHasAttachmentInComments,
      setActiveTab,
      commentsResolver,
      changeSort,
      linkToQuestions
    },
    ref
  ) => {
    const { isDesktop } = useWindowSize();
    const tabs = useMemo(() => {
      const baseTabs: TabItem[] = [
        {
          id: 0,
          title: 'О товаре',
          icon: infoBlackOutlined,
          iconHover: infoWhiteOutlined,
          alt: 'О товаре'
        },
        {
          id: 1,
          title: (
            <span>
              {' '}
              <span className='bold text-inherit'>{averageRating} </span> Отзывы
              ({reviewTotalCount})
            </span>
          ),
          icon: starYellowOutlined,
          iconHover: starYellowOutlined,
          alt: 'Отзывы'
        },
        {
          id: 2,
          title: `Вопросы (${commentTotalCount})`,
          icon: qcBlackOutlined,
          iconHover: qcWhiteOutlined,
          alt: 'Вопросы'
        }
      ];

      if (!isBarMenu) {
        baseTabs.push({
          id: 3,
          title: `Рецепты (${productRecipes.length})`,
          icon: chBlackOutlined,
          iconHover: chWhiteOutlined,
          alt: 'Рецепты'
        });

        baseTabs.push({
          id: 4,
          title: `Видео (${videos?.length ? videos.length : 0})`,
          icon: videoBlackOutlined,
          iconHover: videoWhiteOutlined,
          alt: 'Видео'
        });
      }

      return baseTabs;
    }, [
      averageRating,
      reviewTotalCount,
      commentTotalCount,
      isBarMenu,
      productRecipes?.length,
      videos?.length
    ]);
    return (
      <div ref={ref}>
        <Tabs
          selectedIndex={activeTab}
          onSelect={setActiveTab}
          className='pp-tabs'
          forceRenderTabPanel={IsPrerender()}
        >
          <TabList>
            {tabs.map((item, index) => (
              <Tab key={item.id}>
                <HoveredButton
                  wrapperClassName={`pp-tabs__tab${
                    index === activeTab ? ' pp-tabs__tab--active' : ''
                  }`}
                  text={item.title}
                  icon={item.icon}
                  iconHover={item.iconHover}
                  isActive={index === activeTab}
                  alt={item.alt}
                />
              </Tab>
            ))}
          </TabList>
          <TabPanel>
            <ProductInfoTab
              carbs={carbs}
              proteins={proteins}
              fats={fats}
              reference_weight={reference_weight}
              defaultOpen={isBarMenu ? true : isDesktop}
              how_to_make={how_to_make}
              measureItem={measureItem}
              weight_of_goods={weight_of_goods}
              country={country}
              attributes={attributes}
              productPackage={productPackage}
              nutritional_value={nutritional_value}
              energy_value={energy_value}
              ingredients={ingredients}
              storage_conditions={storage_conditions}
              gost={gost}
              documents={documents}
              text={text}
              isBarMenu={isBarMenu}
              isDigital={isDigital}
              supplier_id={supplier_id}
            />
          </TabPanel>
          <TabPanel>
            <Suspense fallback={<LoadingPage />}>
              <CommentsBlock
                isBarMenu={isBarMenu}
                title='Отзывы'
                bestComment={
                  isMobile && mostUsefulComment ? (
                    <BestComment
                      isClientReviewStatsDisabled={isBarMenu}
                      peopleItem={mostUsefulComment.peopleItem}
                      rating={mostUsefulComment.rating}
                      avatar={
                        mostUsefulComment?.peopleItem?.attachmentsItem
                          ?.cacheItem?.[0]?.filePath
                      }
                      fio={mostUsefulComment?.peopleItem?.fio}
                      date={dayjs(mostUsefulComment.date).format(
                        'DD MMMM YYYY'
                      )}
                      text={mostUsefulComment.text}
                      photos={mostUsefulComment.photos}
                    />
                  ) : null
                }
                showWithFileBtn={isProductHasAttachmentInComments}
                formPlaceholder={'Ваш отзыв о товаре'}
                isShowReviewRules
                modalSuccessTitle='Спасибо за Ваш отзыв.'
                modalSuccessText='Обработка займёт некоторое время.'
                commentsItemsName='commentsItems'
                resolver={commentsResolver}
                objectId={Number(id)}
                productId={+id}
                isSortList
                sortList={sortlist}
                sortListValue={setSort}
                sortListCallback={changeSort}
                sortlistInRow
                productPage
                changeTabToComments={linkToQuestions}
                newTypeComment
                objectType='PRODUCT'
                isProductAvailableForRefund={isProductAvailableForRefund}
                commentType={CommentsTypeTypeEnum.Review}
                withReturnForm
                addCommentInstance='Catalog'
                isVesovoi={isVesovoi}
              />
            </Suspense>
          </TabPanel>
          <TabPanel>
            <Suspense fallback={<LoadingPage />}>
              <CommentsBlock
                isBarMenu={isBarMenu}
                title='Задать вопрос'
                formPlaceholder={'Ваш вопрос'}
                modalSuccessTitle='Спасибо за Ваш вопрос.'
                modalSuccessText='Обработка займет некоторое время, после чего ответ будет направлен на электронную почту.'
                isShowReviewRules
                commentsItemsName='commentsItems'
                resolver={commentsResolver}
                objectId={Number(id)}
                productId={id}
                isSortList
                sortList={sortlist}
                sortListValue={setSort}
                sortListCallback={changeSort}
                sortlistInRow
                productPage
                commentType={CommentsTypeTypeEnum.Comment}
                newTypeComment
                objectType='PRODUCT'
                addCommentInstance='Catalog'
                isQuestion
              />
            </Suspense>
          </TabPanel>
          {/* ЭТАП 1 · Шаг 3 — легаси-баг, подсвеченный SSR-логом: для bar-menu
              tabs режется до 3 (без «Рецепты»/«Видео»), а TabPanel всегда было 5 —
              react-tabs ругался «3 Tab and 5 TabPanel». Панели — под тем же
              условием, что и вкладки. */}
          {!isBarMenu && (
            <TabPanel>
              <Suspense fallback={<LoadingPage />}>
                <ProductPageRecipesTab recipes={productRecipes} />
              </Suspense>
            </TabPanel>
          )}
          {!isBarMenu && (
            <TabPanel>
              <Suspense fallback={<LoadingPage />}>
                <VideoTab videos={videos} />
              </Suspense>
            </TabPanel>
          )}
        </Tabs>
      </div>
    );
  }
);

export default observer(PPTabs);
